import { apiv2Get, apiv2Update } from '@/lib/apiv2';
import { toChatThread, type ChatRow } from '@/logic/chatMessage';

export interface OutreachRow {
  outreach_id?: string | number;
  withings_user_id: string | number;
  channel: 'sms' | 'email' | 'portal' | string;
  direction?: 'inbound' | 'outbound';
  body?: string;
  sent_at?: string;
  source?: string;
  msg_type?: string;
  [key: string]: unknown;
}

// Email audit rows and "open the app" SMS pings are not chat messages; a milestone badge keeps its own ping status.
export async function getCommunicationLog(withingsUserId: string | number): Promise<ChatRow<OutreachRow>[]> {
  const rows = await apiv2Get('outreach', {
    withings_user_id: String(withingsUserId),
    limit: 200,
  });
  return toChatThread((rows as OutreachRow[]) || []);
}

// Bump careteam_last_seen_at to clear dashboard `has_unread`. Best-effort, swallows errors.
export async function markChatViewedByCareteam(withingsUserId: string | number | null | undefined): Promise<void> {
  if (!withingsUserId) return;
  try {
    await apiv2Update('engagement-state', String(withingsUserId), { careteam_last_seen_at: new Date().toISOString() });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ACCESS] careteam_last_seen_at bump failed:', (e as Error)?.message || e);
  }
}

// Reset careteam_last_seen_at to the epoch so any inbound message re-flags dashboard
// `has_unread` (= last_inbound_sent_at > COALESCE(careteam_last_seen_at, '1970-01-01')).
// Mirror of markChatViewedByCareteam. Best-effort, swallows errors.
export async function markChatUnreadByCareteam(withingsUserId: string | number | null | undefined): Promise<void> {
  if (!withingsUserId) return;
  try {
    await apiv2Update('engagement-state', String(withingsUserId), { careteam_last_seen_at: new Date(0).toISOString() });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ACCESS] careteam_last_seen_at reset failed:', (e as Error)?.message || e);
  }
}

// The read-tick watermark the patient app writes (spec 2026-10-13-chat-read-receipt). The patient can
// write it and apiv2 does not check it, so a future or unreadable value means "no signal".
const LAST_SEEN_MAX_SKEW_MS = 5 * 60 * 1000;

export async function getPatientChatLastSeen(withingsUserId: string | number | null | undefined): Promise<string | null> {
  if (!withingsUserId) return null;
  const want = String(withingsUserId);
  const rows = ((await apiv2Get('engagement-state', { withings_user_id: want })) || []) as
    Array<{ withings_user_id?: string | number; id_dev_user?: string | number; patient_last_seen_at?: string | null }>;
  // A dropped filter returns every patient's row: never pick one out of a roster.
  if (rows.some((r) => String(r?.withings_user_id ?? r?.id_dev_user) !== want)) {
    throw new Error(`engagement-state read for ${want} returned another patient's row`);
  }
  const value = rows[0]?.patient_last_seen_at;
  const ms = Date.parse(String(value ?? ''));
  if (!Number.isFinite(ms) || ms > Date.now() + LAST_SEEN_MAX_SKEW_MS) return null;
  return String(value);
}

// Deliberately still a stub: `last_message_at` counts AI + automated sends, so it
// is not a human-interaction signal (that's getLastInteractionDates below).
// PatientListTable renders the empty map as "—".
export async function getLastOutreachDates(): Promise<Map<string, string>> {
  return new Map<string, string>();
}

// Roster-wide `last_inbound_at` (when each patient last sent a chat message) — the
// patient half of Engage's conversation ordering; the care-team half rides on the
// dashboard roster row. ⚠️ Unbounded + cross-program by construction: apiv2 offers
// no limit/programid here. Only read entries for patients the program-scoped roster
// returned. Rationale + apiv2 asks: docs/specs/2026-07-30-engage-conversation-ordering.md
export async function getLastInteractionDates(): Promise<Map<string, string>> {
  const rows = (await apiv2Get('engagement-state', {})) as
    Array<{ withings_user_id?: string | number; id_dev_user?: string | number; last_inbound_at?: string | null }>;
  const out = new Map<string, string>();
  (rows || []).forEach((r) => {
    // Tolerate the raw apiv2 name too, as logic/dashboard.ts does for roster rows —
    // if the respMap shim ever drops, an empty map would silently unsort the list.
    const id = r?.withings_user_id ?? r?.id_dev_user;
    if (id != null && r.last_inbound_at) out.set(String(id), r.last_inbound_at);
  });
  return out;
}
